import { useState, useEffect, useCallback, useRef } from "react";
import { API, POLL_INTERVAL_MS, ADMIN_TOKEN } from "../constants";

const PAGE_SIZE = 20;

// ── Client-side video thumbnail (extract frame via Canvas) ───────────────────
// Returns a JPEG Blob from the first meaningful frame, or null on failure.
async function captureVideoThumb(file) {
  return new Promise((resolve) => {
    const video = document.createElement("video");
    const url   = URL.createObjectURL(file);
    video.muted      = true;
    video.playsInline = true;
    video.preload    = "metadata";

    const cleanup = () => URL.revokeObjectURL(url);

    video.addEventListener("loadedmetadata", () => {
      // Seek to 10% of duration or 1 s, whichever is smaller
      video.currentTime = Math.min(1, (video.duration || 0) * 0.1);
    });

    video.addEventListener("seeked", () => {
      try {
        const MAX = 800;
        const ratio  = Math.min(MAX / video.videoWidth, MAX / video.videoHeight, 1);
        const w = Math.round(video.videoWidth  * ratio);
        const h = Math.round(video.videoHeight * ratio);
        const canvas = document.createElement("canvas");
        canvas.width  = w;
        canvas.height = h;
        canvas.getContext("2d").drawImage(video, 0, 0, w, h);
        canvas.toBlob(
          (blob) => { cleanup(); resolve(blob || null); },
          "image/jpeg",
          0.82
        );
      } catch { cleanup(); resolve(null); }
    });

    video.addEventListener("error", () => { cleanup(); resolve(null); });
    video.src = url;
  });
}

// ── Client-side image compression ───────────────────────────────────────────
// Resizes photos to max 1920px and re-encodes as JPEG 85% before upload.
// Videos and already-small images pass through unchanged.
async function compressImage(file) {
  if (!file.type.startsWith("image/")) return file; // videos: unchanged

  const MAX_DIM = 1920;
  const QUALITY = 0.85;

  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);
      let { naturalWidth: w, naturalHeight: h } = img;

      // Skip compression if already within limits
      if (w <= MAX_DIM && h <= MAX_DIM) { resolve(file); return; }

      const ratio = Math.min(MAX_DIM / w, MAX_DIM / h);
      w = Math.round(w * ratio);
      h = Math.round(h * ratio);

      const canvas = document.createElement("canvas");
      canvas.width  = w;
      canvas.height = h;
      canvas.getContext("2d").drawImage(img, 0, 0, w, h);

      canvas.toBlob(
        (blob) => {
          if (!blob) { resolve(file); return; }
          const baseName = file.name.replace(/\.[^.]+$/, "");
          resolve(new File([blob], `${baseName}.jpg`, { type: "image/jpeg" }));
        },
        "image/jpeg",
        QUALITY
      );
    };

    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });
}

// ── Hook ─────────────────────────────────────────────────────────────────────
export function useGallery(userId, userName) {
  const [photos, setPhotos]               = useState([]);
  const [uploading, setUploading]         = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [error, setError]                 = useState(null);
  const [hasMore, setHasMore]             = useState(true);
  const [loadingMore, setLoadingMore]     = useState(false);
  const pageRef = useRef(0);

  // Fetch a specific page; replace=true resets the list
  const fetchPage = useCallback(async (page, replace = false) => {
    try {
      const r = await fetch(`${API}/api/gallery?page=${page}&limit=${PAGE_SIZE}`);
      if (!r.ok) throw new Error(`Server error: ${r.status}`);
      const data = await r.json();
      if (replace) {
        setPhotos(data.items || []);
        pageRef.current = 0;
      } else {
        setPhotos((prev) => [...prev, ...(data.items || [])]);
        pageRef.current = page;
      }
      setHasMore(data.hasMore ?? false);
    } catch {
      if (replace) setError("Nie udało się załadować galerii.");
    }
  }, []);

  // Initial load + polling for new photos at the top
  useEffect(() => {
    fetchPage(0, true);
    const t = setInterval(async () => {
      try {
        const r = await fetch(`${API}/api/gallery?page=0&limit=${PAGE_SIZE}`);
        if (!r.ok) return;
        const data = await r.json();
        setPhotos((prev) => {
          const ids   = new Set(prev.map((p) => p.id));
          const fresh = (data.items || []).filter((p) => !ids.has(p.id));
          return fresh.length ? [...fresh, ...prev] : prev;
        });
      } catch {}
    }, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [fetchPage]);

  // Called by IntersectionObserver when sentinel enters view
  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    await fetchPage(pageRef.current + 1, false);
    setLoadingMore(false);
  }, [loadingMore, hasMore, fetchPage]);

  // Upload one file (image or video)
  const uploadOne = async (file) => {
    const isVideo    = file.type.startsWith("video/");
    const compressed = isVideo ? file : await compressImage(file);

    const fd = new FormData();
    // IMPORTANT: text fields MUST come before the file so that
    // multer's diskStorage filename callback can read req.body.uploaderName
    fd.append("uploaderName", userName);
    fd.append("uploaderId",   userId);
    fd.append("photo",        compressed);

    // For videos: generate a thumbnail frame in the browser and send it along
    if (isVideo) {
      const thumbBlob = await captureVideoThumb(file);
      if (thumbBlob) fd.append("videoThumb", thumbBlob, "thumb.jpg");
    }

    const r    = await fetch(`${API}/api/gallery/upload`, { method: "POST", body: fd });
    const item = await r.json();
    if (!r.ok) throw new Error(item.error || "Błąd przesyłania");
    if (item.id) setPhotos((prev) => [item, ...prev]);
  };

  const uploadMultiple = async (files) => {
    setUploading(true);
    const errors = [];
    try {
      for (let i = 0; i < files.length; i++) {
        setUploadProgress({ current: i + 1, total: files.length });
        try { await uploadOne(files[i]); }
        catch (e) { errors.push(`${files[i].name}: ${e.message}`); }
      }
      if (errors.length)
        alert(`Nie udało się przesłać ${errors.length} plików:\n\n${errors.join("\n")}`);
    } finally {
      setUploading(false);
      setUploadProgress(null);
    }
  };

  const deletePhoto = async (photo, adminMode) => {
    const r = await fetch(`${API}/api/gallery/${photo.id}`, {
      method:  "DELETE",
      headers: { "Content-Type": "application/json" },
      body:    JSON.stringify({ uploaderId: userId, adminToken: adminMode ? ADMIN_TOKEN : "" }),
    });
    if (!r.ok) {
      const data = await r.json();
      return { error: data.error || "Nie udało się usunąć zdjęcia" };
    }
    setPhotos((prev) => prev.filter((p) => p.id !== photo.id));
    return true;
  };

  const addComment = async (photoId, text) => {
    const r = await fetch(`${API}/api/gallery/${photoId}/comments`, {
      method:  "POST",
      headers: { "Content-Type": "application/json" },
      body:    JSON.stringify({ text, authorName: userName }),
    });
    if (!r.ok) return null;
    const comment = await r.json();
    setPhotos((prev) =>
      prev.map((p) =>
        p.id === photoId
          ? { ...p, comments: [...(p.comments || []), comment] }
          : p
      )
    );
    return comment;
  };

  return {
    photos, uploading, uploadProgress, error,
    hasMore, loadingMore,
    uploadMultiple, deletePhoto, loadMore, addComment,
  };
}
